import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CircleDot, CircleSlash, Files, History, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { QUERY_CONFIG } from "../../constants/QueryConfig";
import { convertUtcToLocal } from "../../helpers/CommonHelpers";
import { useOrganization } from "../../contexts/OrganizationContext";
import { MigrationType, MigrationTypeLabels, MigrationTypeNouns } from "../../models/Statistics/MigrationType";
import { UserRole } from "../../models/User";
import { SourceLinksExportFilter } from "../../repositories/JobsRepository";
import {
  SourceLink,
  SourceLinkState,
  SourceLinkStateLabels,
  formatCyclesSinceLastSeen,
  hasFailedBefore,
  isOutstanding,
  removalReasonLabel,
  hasRegressed,
  SourceLinkOutcome,
  SourceLinkOutcomeLabels,
  SourceLinkOutcomeVariants,
  SourceLinkOwner,
  SourceLinkOwnerLabels,
  SourceLinkReason,
  SourceLinkReasonLabels,
} from "../../models/SourceLink";
import SourceLinksRepository from "../../repositories/SourceLinksRepository";
import { FilterCombobox } from "../common/FilterCombobox";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import TableEmptyState from "../common/TableEmptyState";
import CursorPagination from "../common/Table/CursorPagination";
import { SearchInput } from "../common/Table/SearchInput";
import {
  fileFiltersBoxClassName,
  filterComboboxClassName,
  filterSearchClassName,
  tableWrapperClassName,
} from "../common/Table/TableStyles";
import MigrationEntityCell from "../RenamedFolders/MigrationEntityCell";
import SourceReferenceCell from "./SourceReferenceCell";
import SourceLinkFilesModal from "./SourceLinkFilesModal";
import SourceLinkSummaryTiles from "./SourceLinkSummaryTiles";
import SourceRootsSection from "./SourceRootsSection";
import OrganizationsRepository from "../../repositories/OrganizationsRepository";

/** The five tables the registry holds, one tab each. The folder discoveries report against the case and provider tables. */
const entityTypes = [
  MigrationType.CasesMigration,
  MigrationType.IntakesMigration,
  MigrationType.ProvidersMigration,
  MigrationType.FirmDocumentsMigration,
  MigrationType.TemplatesMigration,
];

// "No regression recorded" rather than "Not a regression": a client running without its cache has no memory of
// the previous scan, so every link it reports has an unknown previous outcome, and the filter cannot tell the two
// apart. Naming the option for the absent record keeps the pair an honest partition.
const regressedOptions = [
  { value: "regressed", label: "Broke since last cycle" },
  { value: "steady", label: "No regression recorded" },
];

// A history question, independent of the state: the one way to find the healthy links that used to fail, since
// "recovered" is not a state of its own.
const failedBeforeOptions = [
  { value: "failed", label: "Has failed before" },
  { value: "never", label: "Never failed" },
];

/**
 * Says in words what the current filter selects, for the export confirmation. The export follows the filter
 * rather than the page, so with nothing narrowed it is the type's whole table — millions of rows for a large firm.
 */
const describeFilter = (filters: {
  /** `null` for an export of every type the registry holds. */
  entityType: MigrationType | null;
  outcomeFilter: SourceLinkOutcome | null;
  reasonFilter: SourceLinkReason | null;
  ownerFilter: SourceLinkOwner | null;
  stateFilter: SourceLinkState | null;
  regressedFilter: string | null;
  failedBeforeFilter: string | null;
  searchQuery: string;
}): string => {
  const parts = [
    filters.entityType === null ? "all entity types" : MigrationTypeLabels[filters.entityType],
    filters.outcomeFilter && SourceLinkOutcomeLabels[filters.outcomeFilter],
    filters.reasonFilter && SourceLinkReasonLabels[filters.reasonFilter],
    filters.ownerFilter && SourceLinkOwnerLabels[filters.ownerFilter],
    filters.stateFilter && SourceLinkStateLabels[filters.stateFilter],
    filters.regressedFilter === "regressed" ? "broke since last cycle" : null,
    filters.regressedFilter === "steady" ? "not a regression" : null,
    filters.failedBeforeFilter === "failed" ? "has failed before" : null,
    filters.failedBeforeFilter === "never" ? "never failed" : null,
    filters.searchQuery ? `matching "${filters.searchQuery}"` : null,
  ].filter(Boolean);

  return `links narrowed to ${parts.join(", ")}`;
};

// Every outcome, the two success values included: under full-map reporting the healthy links are in the
// registry too, and the state filter is what keeps them out of the default view.
const outcomeOptions = Object.values(SourceLinkOutcome).map((outcome) => ({
  value: outcome,
  label: SourceLinkOutcomeLabels[outcome],
}));

const reasonOptions = Object.values(SourceLinkReason).map((reason) => ({
  value: reason,
  label: SourceLinkReasonLabels[reason],
}));

const ownerOptions = [
  SourceLinkOwner.FirmData,
  SourceLinkOwner.Environment,
  SourceLinkOwner.TrackedElsewhere,
  SourceLinkOwner.DataQuality,
  SourceLinkOwner.Intended,
  SourceLinkOwner.None,
].map((owner) => ({ value: owner, label: SourceLinkOwnerLabels[owner] }));

/**
 * The filter's "All" entry. It needs a value of its own rather than the combobox's null, which reads as nothing
 * chosen and shows the placeholder; the query takes null, so the two are mapped at that boundary.
 */
const ALL_STATES = "all";

// The four outstanding kinds first, in the order the summary shows them, so each figure and its filter option sit
// in the same place. Healthy, gone and All last: they may read the bulk of a full-map table, the page opens on none
// of them, and their totals come back capped rather than exact.
const stateOptions = [
  { value: SourceLinkState.MissingOrUnreachable, label: "Missing or unreachable" },
  { value: SourceLinkState.InvalidPath, label: "Invalid paths" },
  { value: SourceLinkState.Duplicate, label: "Duplicates" },
  { value: SourceLinkState.Excluded, label: "Excluded" },
  { value: SourceLinkState.Healthy, label: "Healthy" },
  { value: SourceLinkState.RemovedFromSource, label: "Gone from source" },
  { value: ALL_STATES, label: "All" },
];

/** The link's state as one badge; a healthy link that once failed also says so, since that is no longer a state. */
const StateCell: React.FC<{ link: SourceLink }> = ({ link }) => {
  if (link.state === SourceLinkState.RemovedFromSource) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="neutral">
            <CircleSlash className="size-3" />
            Gone from source
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm">
          {link.removalReason
            ? removalReasonLabel(link.removalReason, link.migrationType)
            : "The source no longer offers this link"}
          , so there is nothing left to fix.
        </TooltipContent>
      </Tooltip>
    );
  }

  if (link.state === SourceLinkState.Healthy) {
    return (
      <div className="flex flex-col gap-0.5 items-start">
        <Badge variant="success">
          <ShieldCheck className="size-3" />
          Healthy
        </Badge>
        {hasFailedBefore(link) && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center gap-1 text-xs text-muted-foreground cursor-default">
                <History className="size-3" />
                failed before
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              This link works now. It failed on an earlier cycle, and the reason shown is what went wrong then.
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  }

  return (
    <Badge variant={link.state === SourceLinkState.MissingOrUnreachable ? "warning" : "neutral"}>
      <CircleDot className="size-3" />
      {SourceLinkStateLabels[link.state] ?? link.state}
    </Badge>
  );
};

/** What an export started now would contain: the table's current filters, and a sentence naming them. */
export interface SourceLinksExportScope {
  filter: SourceLinksExportFilter;
  summary: string;
  /** The same filters across every entity type, not only the open tab. */
  allTypesSummary: string;
}

interface SourceLinksTableProps {
  userRole: UserRole;
  /** Told whenever the filters change, so an export can be started from outside the table with the same view. */
  onExportScopeChange?: (scope: SourceLinksExportScope) => void;
}

export const SourceLinksTable: React.FC<SourceLinksTableProps> = ({ onExportScopeChange }) => {
  const repository = new SourceLinksRepository();
  // Built during render, not inside queryFn: useApi() calls React hooks, so constructing a repository outside a
  // render invokes them out of context and the query silently yields nothing.
  const organizationsRepository = new OrganizationsRepository();
  const { selectedOrganizationId } = useOrganization();
  const navigate = useNavigate();

  const [entityType, setEntityType] = useState<MigrationType>(MigrationType.CasesMigration);
  const [outcomeFilter, setOutcomeFilter] = useState<SourceLinkOutcome | null>(null);
  const [reasonFilter, setReasonFilter] = useState<SourceLinkReason | null>(null);
  const [ownerFilter, setOwnerFilter] = useState<SourceLinkOwner | null>(null);
  // Opens on the kind worth looking at, which is also the summary's headline figure. Healthy and All are one filter
  // away, never the default: for a firm reporting the full map they are the bulk of the table.
  const [stateSelection, setStateSelection] = useState<string>(SourceLinkState.MissingOrUnreachable);
  const stateFilter = stateSelection === ALL_STATES ? null : (stateSelection as SourceLinkState);
  const [regressedFilter, setRegressedFilter] = useState<string | null>(null);
  const [failedBeforeFilter, setFailedBeforeFilter] = useState<string | null>(null);
  const failedBefore = failedBeforeFilter === null ? null : failedBeforeFilter === "failed";
  const [searchQuery, setSearchQuery] = useState("");
  const [descending, setDescending] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  // The cursors walked to reach the current page; its length is the page index and its top is the page's start.
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [links, setLinks] = useState<SourceLink[]>([]);
  const [drillDown, setDrillDown] = useState<SourceLink | null>(null);

  const resetPaging = () => setCursors([null]);

  // Constant across every row, so fetched once rather than carried on each link.
  const implementationTypeQuery = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION_IMPLEMENTATION_TYPE, selectedOrganizationId],
    queryFn: () => organizationsRepository.getOrganizationImplementationType(selectedOrganizationId!),
    staleTime: Infinity,
    enabled: !!selectedOrganizationId,
  });

  const summaryQuery = useQuery({
    queryKey: [QUERY_KEYS.SOURCE_LINK_SUMMARY, selectedOrganizationId],
    queryFn: () => repository.getSummary(),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    enabled: !!selectedOrganizationId,
  });

  const after = cursors[cursors.length - 1];

  const linksQuery = useQuery({
    queryKey: [
      QUERY_KEYS.SOURCE_LINKS,
      selectedOrganizationId,
      entityType,
      outcomeFilter,
      reasonFilter,
      ownerFilter,
      stateFilter,
      regressedFilter,
      failedBeforeFilter,
      searchQuery,
      after,
      rowsPerPage,
      descending,
    ],
    queryFn: () =>
      repository.getSourceLinks({
        entityType,
        outcome: outcomeFilter,
        reason: reasonFilter,
        owner: ownerFilter,
        state: stateFilter,
        regressed: regressedFilter === null ? null : regressedFilter === "regressed",
        failedBefore,
        search: searchQuery || null,
        after,
        pageSize: rowsPerPage,
        descending,
      }),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
    refetchOnMount: true,
    enabled: !!selectedOrganizationId,
  });

  useEffect(() => {
    if (linksQuery.data) setLinks(linksQuery.data.items);
  }, [linksQuery.data]);

  // A failed request is not a load in progress: folding the two together leaves the table dimmed and inert with
  // nothing saying why.
  const loading = linksQuery.isFetching;
  const page = linksQuery.data;
  const summary = summaryQuery.data?.find((entry) => entry.entityType === entityType);
  const nouns = MigrationTypeNouns[entityType];

  // An empty page means different things with and without a filter: nothing matches, or nothing was ever reported.
  const otherFilters =
    outcomeFilter !== null ||
    reasonFilter !== null ||
    ownerFilter !== null ||
    regressedFilter !== null ||
    failedBeforeFilter !== null ||
    searchQuery !== "";
  const narrowed = stateFilter !== null || otherFilters;

  useEffect(() => {
    const described = {
      outcomeFilter,
      reasonFilter,
      ownerFilter,
      stateFilter,
      regressedFilter,
      failedBeforeFilter,
      searchQuery,
    };
    onExportScopeChange?.({
      filter: {
        entityType,
        outcome: outcomeFilter,
        failureReason: reasonFilter,
        state: stateFilter,
        owner: ownerFilter,
        regressed: regressedFilter === null ? null : regressedFilter === "regressed",
        failedBefore,
        search: searchQuery || null,
      },
      summary: describeFilter({ entityType, ...described }),
      allTypesSummary: describeFilter({ entityType: null, ...described }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the callback is the parent's setter
  }, [entityType, outcomeFilter, reasonFilter, ownerFilter, stateFilter, regressedFilter, failedBeforeFilter, searchQuery]);

  // A figure clicked in the summary lands on exactly the rows it counts, so every narrowing it does not know about
  // is dropped. Search stays: it is visible in its box, so nothing is narrowed out of sight.
  const selectState = (state: SourceLinkState) => {
    setStateSelection(state);
    setOutcomeFilter(null);
    setReasonFilter(null);
    setOwnerFilter(null);
    setRegressedFilter(null);
    setFailedBeforeFilter(null);
    resetPaging();
  };

  return (
    // Scrolls as a whole when the viewport is short: the tiles, the roots and two rows of filters sit above the
    // table and do not shrink, so without this the table body is what gives way and can reach zero rows while
    // its pagination still reports a page.
    <div className="flex flex-col w-full flex-1 min-h-0 gap-3 overflow-y-auto">
      <Tabs
        value={entityType}
        onValueChange={(next) => {
          setEntityType(next as MigrationType);
          resetPaging();
        }}
      >
        <TabsList>
          {entityTypes.map((type) => {
            const missing = summaryQuery.data?.find((entry) => entry.entityType === type)?.outstanding
              .missingOrUnreachable;
            return (
              <TabsTrigger key={type} value={type} className="gap-2">
                {MigrationTypeLabels[type]}
                {missing !== undefined && missing > 0 && (
                  <Badge variant="danger" className="px-1.5 py-0 text-[10px]">
                    {new Intl.NumberFormat().format(missing)}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      <SourceLinkSummaryTiles
        summary={summary}
        loading={summaryQuery.isFetching}
        entityType={entityType}
        onSelectState={selectState}
      />

      {selectedOrganizationId && <SourceRootsSection organizationId={selectedOrganizationId} entityType={entityType} />}

      <div className={fileFiltersBoxClassName}>
        <div className={filterSearchClassName}>
          <SearchInput
            label="Search"
            placeholder={`Link path or ${nouns.one}`}
            onSearch={(value) => {
              setSearchQuery(value);
              resetPaging();
            }}
          />
        </div>
        <div className={filterComboboxClassName}>
          <FilterCombobox
            value={outcomeFilter}
            onChange={(value) => {
              setOutcomeFilter(value as SourceLinkOutcome | null);
              resetPaging();
            }}
            options={outcomeOptions}
            placeholder="All outcomes"
          />
        </div>
        <div className={filterComboboxClassName}>
          <FilterCombobox
            value={reasonFilter}
            onChange={(value) => {
              setReasonFilter(value as SourceLinkReason | null);
              resetPaging();
            }}
            options={reasonOptions}
            placeholder="All reasons"
          />
        </div>
        <div className={filterComboboxClassName}>
          <FilterCombobox
            value={ownerFilter}
            onChange={(value) => {
              setOwnerFilter(value as SourceLinkOwner | null);
              resetPaging();
            }}
            options={ownerOptions}
            placeholder="Anyone"
          />
        </div>
        <div className={filterComboboxClassName}>
          <FilterCombobox
            value={stateSelection}
            onChange={(value) => {
              // Clearing the combobox means the same thing as picking All.
              setStateSelection(value ?? ALL_STATES);
              resetPaging();
            }}
            options={stateOptions}
            placeholder="All"
          />
        </div>
        <div className={filterComboboxClassName}>
          <FilterCombobox
            value={regressedFilter}
            onChange={(value) => {
              setRegressedFilter(value as string | null);
              resetPaging();
            }}
            options={regressedOptions}
            placeholder="Any change"
          />
        </div>
        <div className={filterComboboxClassName}>
          <FilterCombobox
            value={failedBeforeFilter}
            onChange={(value) => {
              setFailedBeforeFilter(value as string | null);
              resetPaging();
            }}
            options={failedBeforeOptions}
            placeholder="Any history"
          />
        </div>
      </div>

      <div className={cn(tableWrapperClassName, "min-h-[16rem]", loading && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[15%]">
                {nouns.one.charAt(0).toUpperCase()}
                {nouns.one.slice(1)}
              </TableHead>
              <TableHead className="w-[25%]">Link</TableHead>
              <TableHead className="w-[13%]">Source record</TableHead>
              <TableHead className="w-[13%]">Outcome</TableHead>
              <TableHead className="w-[11%]">Reason</TableHead>
              <TableHead className="w-[9%]">
                <SortableTableColumnHeader
                  label="Last reported"
                  sortProperty="lastSeenOn"
                  getSort={() => "lastSeenOn"}
                  setSort={() => undefined}
                  getOrder={() => (descending ? "desc" : "asc")}
                  setOrder={(next) => {
                    setDescending(next === "desc");
                    resetPaging();
                  }}
                />
              </TableHead>
              <TableHead className="w-[9%]">State</TableHead>
              <TableHead className="w-[5%]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {links.map((link) => (
              <TableRow key={link.id}>
                <TableCell>
                  <MigrationEntityCell
                    entityType={link.entityType}
                    migrationEntityId={link.migrationEntityId}
                    entityLabel={link.entityLabel}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex flex-col min-w-0">
                    <span className="truncate font-medium" title={link.linkPath}>
                      {link.linkPath}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <SourceReferenceCell link={link} implementationType={implementationTypeQuery.data} />
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-0.5 items-start">
                    <Badge variant={SourceLinkOutcomeVariants[link.outcome] as never}>
                      {SourceLinkOutcomeLabels[link.outcome] ?? link.outcome}
                    </Badge>
                    {link.outcome === SourceLinkOutcome.PartiallyDiscovered && (
                      <span className="text-xs text-muted-foreground">
                        {link.filesProducedCount} found · {link.subPathFailureCount} missed
                      </span>
                    )}
                    {link.outcome === SourceLinkOutcome.FilesDiscovered && link.filesProducedCount > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {link.filesProducedCount} file{link.filesProducedCount === 1 ? "" : "s"}
                      </span>
                    )}
                    {/* Both links name the same target, so what is worth showing is which record claimed it:
                        the source tab drives the doc type the file was filed under. */}
                    {link.supersededBySourceTable && (
                      <span className="text-xs text-muted-foreground">
                        claimed by {link.supersededBySourceTable} ·{" "}
                        <span className="font-mono">
                          {link.supersededBySourceRecordColumn} = {link.supersededBySourceRecordId}
                        </span>
                      </span>
                    )}
                    {hasRegressed(link) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="warning" className="cursor-default">
                            Broke since last cycle
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm">
                          This worked on the last cycle and does not now. Something changed at the source.
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {link.outcome === SourceLinkOutcome.DuplicateTarget &&
                      !link.supersededBySourceTable && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-xs text-muted-foreground cursor-default">
                              winner not identified
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm">
                            The file arrived, through a different link that reached it first. We cannot tell which
                            record it came from.
                          </TooltipContent>
                        </Tooltip>
                      )}
                  </div>
                </TableCell>
                <TableCell>
                  {link.reason === SourceLinkReason.PathFileLimit ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="link"
                          className="h-auto p-0 text-left"
                          onClick={() =>
                            navigate(`/organization/${selectedOrganizationId}/flagged-paths`)
                          }
                        >
                          {SourceLinkReasonLabels[link.reason]}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        Over the per-path file limit. This path is already recorded as a flagged path, and that is
                        where it is allowed or left excluded &mdash; open Flagged Paths.
                      </TooltipContent>
                    </Tooltip>
                  ) : link.reason ? (
                    link.reasonDetail ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="underline decoration-dotted underline-offset-2">
                            {SourceLinkReasonLabels[link.reason] ?? link.reason}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm break-words">{link.reasonDetail}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <span>{SourceLinkReasonLabels[link.reason] ?? link.reason}</span>
                    )
                  ) : (
                    <span className="text-muted-foreground">&mdash;</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span>{convertUtcToLocal(link.lastSeenOn)}</span>
                    {/* A timestamp does not say whether anybody is still looking; the age in cycles is what
                        distinguishes a live problem from one whose entity left discovery. The date can be old on a
                        link that is fine: under the full map an unchanged link is not re-sent, only confirmed, and
                        the age counts the confirmation. Shown for outstanding links, where it prompts a question. */}
                    {isOutstanding(link.state) &&
                      link.cyclesSinceLastSeen !== undefined &&
                      link.cyclesSinceLastSeen > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-xs text-amber-700 dark:text-amber-300 cursor-default">
                              not seen for {formatCyclesSinceLastSeen(link.cyclesSinceLastSeen)} cycle
                              {link.cyclesSinceLastSeen === 1 ? "" : "s"}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm">
                            Not seen for {formatCyclesSinceLastSeen(link.cyclesSinceLastSeen)} cycle
                            {link.cyclesSinceLastSeen === 1 ? "" : "s"}.
                            A link is accounted for on every cycle while its {MigrationTypeNouns[link.migrationType]?.one ?? "record"} is
                            still discovered, so this usually means the{" "}
                            {MigrationTypeNouns[link.migrationType]?.one ?? "record"} has left discovery — check whether
                            it was closed or excluded.
                          </TooltipContent>
                        </Tooltip>
                      )}
                  </div>
                </TableCell>
                <TableCell>
                  <StateCell link={link} />
                </TableCell>
                <TableCell>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon-sm" onClick={() => setDrillDown(link)} aria-label="Files from this link">
                        <Files className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Files discovered through this link</TooltipContent>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {linksQuery.isError && !linksQuery.isFetching && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={99}>
                  <TableEmptyState
                    icon={AlertTriangle}
                    title="Couldn't load source links"
                    description="The links could not be loaded. Nothing was changed."
                    action={
                      <Button variant="outline" size="sm" onClick={() => linksQuery.refetch()}>
                        Try again
                      </Button>
                    }
                  />
                </TableCell>
              </TableRow>
            )}
            {links.length === 0 && !loading && !linksQuery.isError && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={99}>
                  <TableEmptyState
                    title="No source links match"
                    description={
                      narrowed
                        ? stateFilter === SourceLinkState.MissingOrUnreachable && !otherFilters
                          ? `No ${nouns.one} link is missing or unreachable. Change the state filter to see the other kinds, or links that work or have gone.`
                          : "No link of this type matches these filters."
                        : "Every link a cycle reports for this type appears here once one has run."
                    }
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <CursorPagination
          pageIndex={cursors.length - 1}
          rowsPerPage={rowsPerPage}
          rowsOnPage={links.length}
          totalCount={page?.totalCount ?? links.length}
          totalCountIsCapped={page?.totalCountIsCapped ?? false}
          hasNext={!!page?.nextCursor}
          onPrevious={() => setCursors((stack) => (stack.length > 1 ? stack.slice(0, -1) : stack))}
          onNext={() => page?.nextCursor && setCursors((stack) => [...stack, page.nextCursor!])}
          onRowsPerPageChange={(next) => {
            setRowsPerPage(next);
            resetPaging();
          }}
        />
      </div>

      {selectedOrganizationId && (
        <SourceLinkFilesModal link={drillDown} organizationId={selectedOrganizationId} onClose={() => setDrillDown(null)} />
      )}
    </div>
  );
};

export default SourceLinksTable;
